import { CURRENCY_SYMBOL } from "@/Config/index";
import { DOMAIN_CART_PAGE, DOMAIN_LANDING_PAGE, DOMAIN_REQUEST_TO_CALLBACK } from "../../../Constants/routesConstant";
import store from "../../../Redux/Store";
import { getAuthToken } from "../../../Utils/authToken";
import { DOMAIN_SUBSCRIPTION_CART } from "../../../Utils/constants";
import history from "../../../Utils/history";
import LocalStorageHelper from "../../../Utils/LocalStorageHelper";
import { DLP_BANNER_UPSELL_FEATURE_TYPE, MIN_PERCENTAGE_CHECK } from "./constant";

export const DEFAULT_DOMAIN_CART = {
  cart_id: 0,
  type: 0,
  amount: 0,
  discounted_amount: 0,
  items: [],
};

export const calculateDiscountPercentage = (obj) => {
  const discount = obj?.discount;
  const netSellingPrice = obj?.total ? obj?.total : obj?.net_sellingprice + obj?.discount;
  if (discount !== undefined && netSellingPrice !== undefined && netSellingPrice !== 0) {
    const discountPercentage = Math.round(discount / netSellingPrice * 100);
    return discountPercentage;
  } else {
    console.error("Unable to calculate discount percentage. Missing or invalid data.");
  }
};

export const hasStringAfterLastDot = (inputString, returnStr = false) => {
  if (!inputString) {
    return false;
  }
  const dots = inputString.split(".").length - 1;

  if (dots === 1) {
    // Check if there is a string after the dot
    const dotIndex = inputString.indexOf(".");
    if (dotIndex < inputString.length - 1) {
      return returnStr
        ? { sld: inputString?.slice(0, dotIndex), tld: inputString?.slice(dotIndex + 1, inputString.length) }
        : true;
    }
  } else if (dots === 2) {
    // Check if the last dot has a string after it
    const lastDotIndex = inputString.lastIndexOf(".");
    if (lastDotIndex < inputString.length - 1) {
      return returnStr
        ? { sld: inputString?.slice(0, lastDotIndex), tld: inputString?.slice(lastDotIndex + 1, inputString.length) }
        : true;
    }
  }

  return false;
};

export const checkDomainCartEmpty = () => {
  // If the user is logged out, then check if there exists a cart data in localstorage
  if (!getAuthToken()) {
    return !LocalStorageHelper.get("domainCart");
  }

  // If the user is logged in, then fetch the cart data from reducer
  const domainCartDetails = store.getState().domainReducer?.domainCart;
  return !domainCartDetails?.items?.length;
};

export const getDomainCart = () => {
  if (!getAuthToken()) {
    return LocalStorageHelper.get("domainCart") || DEFAULT_DOMAIN_CART;
  }

  // If the user is logged in, then fetch the cart data from reducer
  const domainCartDetails = store.getState().domainReducer?.domainCart;
  return domainCartDetails;
};

export const getTLD = (domain) => {
  // Split the domain by dots
  const domainParts = domain.split(".");

  // Get the last part, assuming it is the TLD
  const tld = "." + domainParts.slice(-1)[0];

  return tld.toLowerCase(); // Convert to lowercase for consistency
};

export const calculateDomainCartDetails = (oldCart, newItem) => {
  const result = {
    amount: 0,
    discount: 0,
    items: [],
  };

  const { amount: currentCartAmount, discounted_amount: currentCartDiscount, items: currentCartItems } = oldCart;

  const newItemPayload = {
    cart_item_id: DOMAIN_SUBSCRIPTION_CART.DEFAULT_CART_ITEM_ID,
    subscription_id: DOMAIN_SUBSCRIPTION_CART.SUBSCRIPTION_ID,
    price: newItem?.net_sellingprice || 0,
    is_available: true,
    discounted_price: newItem?.net_sellingprice || 0,
    data: {
      domain: newItem?.domain_name || "",
      purchase_type: DOMAIN_SUBSCRIPTION_CART.DEFAULT_PURCHASE_TYPE,
      year: DOMAIN_SUBSCRIPTION_CART.DEFAULT_YEAR,
      renewal_per_year_price: newItem?.renewal_price,
      purchase_per_year_price: newItem?.net_sellingprice,
      purchase_per_year_mrp: newItem?.purchase_price,
    },
  };

  result.amount = currentCartAmount + newItem?.net_sellingprice;
  result.discount = currentCartDiscount + newItem?.net_sellingprice;
  result.items = [...currentCartItems, newItemPayload];
  return result;
};

export const calculateDomainCartDetailsFromLocalStorage = (newItem) => {
  const result = {
    amount: 0,
    discount: 0,
    items: [],
  };

  const domainCart = LocalStorageHelper.get("domainCart");

  const newItemPayload = {
    cart_item_id: DOMAIN_SUBSCRIPTION_CART.DEFAULT_CART_ITEM_ID,
    subscription_id: DOMAIN_SUBSCRIPTION_CART.SUBSCRIPTION_ID,
    price: newItem?.net_sellingprice || 0,
    is_available: true,
    discounted_price: newItem?.net_sellingprice || 0,
    data: {
      domain: newItem?.domain_name || "",
      purchase_type: DOMAIN_SUBSCRIPTION_CART.DEFAULT_PURCHASE_TYPE,
      year: DOMAIN_SUBSCRIPTION_CART.DEFAULT_YEAR,
      renewal_per_year_price: newItem?.renewal_price,
      purchase_per_year_price: newItem?.net_sellingprice,
      purchase_per_year_mrp: newItem?.purchase_price,
    },
  };

  if (!domainCart) {
    // If domain cart not found in localstorage, that means first time cart is being populated, hence just return this item.
    return {
      amount: newItem?.net_sellingprice,
      discount: newItem?.net_sellingprice,
      items: [newItemPayload],
    };
  }

  // If domain cart found in localstorage, then fetch the existing data and add new item to it.
  const { amount: currentCartAmount, discounted_amount: currentCartDiscount, items: currentCartItems } = domainCart;

  result.amount = currentCartAmount + newItem?.net_sellingprice;
  result.discount = currentCartDiscount + newItem?.net_sellingprice;
  result.items = [...currentCartItems, newItemPayload];
  return result;
};

export const removeDomainCartFromLocalStorage = () => {
  LocalStorageHelper.delete("domainCart");
  return;
};

/**
 *
 * @returns An object with key - domain name and value - it's data received from search API.
 */
export const getDomainsInCart = () => {
  if (!getAuthToken()) {
    return LocalStorageHelper.get("selectedDomainsInCart") || {};
  } else {
    return store.getState().domainReducer?.selectedDomainsInCart;
  }
};

export const saveSelectedDomainData = (domain, callback) => {
  if (!getAuthToken()) {
    const currentSavedDomainData = LocalStorageHelper.get("selectedDomainsInCart") || {};
    currentSavedDomainData[domain.domain_name] = domain;
    LocalStorageHelper.add("selectedDomainsInCart", currentSavedDomainData);
  } else {
    const currentSavedDomainData = store.getState().domainReducer?.selectedDomainsInCart;
    currentSavedDomainData[domain.domain_name] = domain;
    callback && callback(currentSavedDomainData);
  }
};

export const domainExistsInCart = (domain) => {
  const currentCart = getDomainCart();
  return currentCart?.items?.filter((item) => item?.data?.domain === domain?.domain_name)?.length > 0;
};

export const getDomainCartSize = () => {
  const currentCart = getDomainCart();
  return currentCart?.items?.length;
};

// export const checkIsStaging = () => process?.env?.REACT_APP_CUSTOM_ENV !== "PROD";

export const checkIsStaging = () =>
  window.location.href.includes("website.digitaldukaan.app") || window.location.href.includes("localhost");

export const addYearsToCurrentDate = (yearsToAdd) => {
  const currentDate = new Date();
  const newDate = new Date(currentDate.getFullYear() + yearsToAdd, currentDate.getMonth(), currentDate.getDate());
  const month = newDate.getMonth() + 1;
  const year = newDate.getFullYear();
  return { month: month, year: year };
};

export const goToDomainCart = () => {
  const currentSearchParams = new URLSearchParams(window.location.search);
  // Create URLSearchParams for the redirect URL
  const redirectSearchParams = new URLSearchParams();
  // Iterate over current parameters and append them to the redirect URL
  currentSearchParams.forEach((value, key) => {
    redirectSearchParams.append(key, value);
  });
  const redirectURL = `${DOMAIN_LANDING_PAGE}${DOMAIN_CART_PAGE}?${redirectSearchParams.toString()}`;
  history.push(redirectURL);
};

export const renderOriginalPrice = (net_sellingprice, discount) =>
  discount === 0 ? null : 
    <p className="original_price">
      {CURRENCY_SYMBOL}
      {net_sellingprice + discount}
    </p>
  ;

export const checkToShowPercentage = (data) => calculateDiscountPercentage(data) < MIN_PERCENTAGE_CHECK;

export const PAYMENT_OPTIONS = {
  VISA: `${process.env.REACT_APP_BASE_CDN_URL}longtail/custom_website/8474898/RM0kyw72.png`,
  AMEX: `${process.env.REACT_APP_BASE_CDN_URL}longtail/custom_website/8474898/rfVV51nc.png`,
  MASTER_CARD: `${process.env.REACT_APP_BASE_CDN_URL}longtail/custom_website/8474898/40Sz71lk.png`,
  UPI: `${process.env.REACT_APP_BASE_CDN_URL}longtail/custom_website/8474898/05XNZSlU.png`,
};

export function getUtmSource() {
  const params = new URLSearchParams(window.location.search);
  // Parse current URL's search parameters
  const currentSearchParams = new URLSearchParams(window.location.search);
  // Create URLSearchParams for the redirect URL
  const redirectSearchParams = new URLSearchParams();
  // Iterate over current parameters and append them to the redirect URL
  currentSearchParams.forEach((value, key) => {
    redirectSearchParams.append(key, value);
  });
  return redirectSearchParams.toString() || "";
}

export const handleRequestToCallback = () =>
  history.push(
    `${DOMAIN_LANDING_PAGE}${DOMAIN_REQUEST_TO_CALLBACK}${getUtmSource()?.length ? `?${getUtmSource()}` : ""}`,
  );

export const getDLPThemeUpsellDiscount = () => {
  const storeInfo = store.getState().storeReducer.store;
  const {
    brand_plus_upsell_discount = 0,
    brand_pro_upsell_discount = 0,
    ecomm_elite_upsell_discount = 0,
    ecomm_plus_upsell_discount = 0,
    ecomm_upsell_discount = 0,
  } = storeInfo?.constants || {};

  const discountAmount = Math.max(
    ecomm_elite_upsell_discount,
    ecomm_plus_upsell_discount,
    ecomm_upsell_discount,
    brand_pro_upsell_discount,
    brand_plus_upsell_discount,
  );
  return discountAmount;
};

export const showThemeUpsellTimer = () => {
  const bannerInfo = store.getState().staffManagementReducer?.staffMemberData?.banner_info;
  if (bannerInfo) {
    const { expiry_time = "", feature_name = "" } = bannerInfo;
    return !!(expiry_time && feature_name && feature_name === DLP_BANNER_UPSELL_FEATURE_TYPE.THEME);
  }

  return false;
};
